<template>
  <div class="view-inner">
    <!-- Header -->
    <div class="page-header">
      <div>
        <h1 class="page-header-title"><i class="bi bi-people"></i> Gestión de Personal</h1>
        <p class="page-header-sub">Registro y administración del personal operativo</p>
      </div>
      <div class="page-header-actions">
        <button class="btn-add" @click="abrirModalPersonal()">
          <i class="bi bi-plus-lg"></i> Agregar Personal
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="filter-card">
      <div class="filter-fields">
        <div class="filter-field">
          <label class="filter-label">Nombre</label>
          <input type="text" class="form-control form-control-sm" v-model="filtros.nombre"
            placeholder="Buscar por nombre" @input="filtrarPersonal" />
        </div>
        <div class="filter-field">
          <label class="filter-label">Cargo</label>
          <select class="form-select form-select-sm" v-model="filtros.cargo" @change="filtrarPersonal">
            <option value="">Todos</option>
            <option value="CHOFER">Chofer</option>
            <option value="AUXILIAR">Auxiliar</option>
            <option value="ADMINISTRATIVO">Administrativo</option>
            <option value="OPERADOR">Operador</option>
          </select>
        </div>
        <div class="filter-field">
          <label class="filter-label">N° Documento</label>
          <input type="text" class="form-control form-control-sm" v-model="filtros.documento"
            placeholder="Buscar por DNI/CE" @input="filtrarPersonal" />
        </div>
      </div>
      <div class="filter-actions">
        <button class="btn-clear" @click="limpiarFiltros"><i class="bi bi-x-circle"></i> Limpiar</button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="loading-box">
      <span class="spinner-border spinner-border-sm text-primary me-2"></span> Cargando...
    </div>

    <!-- Tabla (estándar: ESTANDAR_LISTADOS.md) -->
    <div v-else>
      <div class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>Nombre Completo</th>
              <th>Cargo</th>
              <th>Tipo Doc.</th>
              <th>N° Documento</th>
              <th>Correo</th>
              <th>Licencia</th>
              <th>Usuario</th>
              <th>Contacto</th>
              <th class="col-acciones">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in paginar(personalFiltrado)" :key="p.id_personal">
              <td class="celda-entidad"><div class="entidad-nombre" :title="`${p.nombre} ${p.apellidos}`">{{ p.nombre }} {{ p.apellidos }}</div></td>
              <td><span class="badge-pill" :class="getBadgeClass(p.cargo)">{{ p.cargo }}</span></td>
              <td class="text-muted">{{ p.tipo_doc_identidad }}</td>
              <td class="celda-id">{{ p.nro_doc_identidad }}</td>
              <td class="celda-entidad">
                <div v-if="p.correo" class="entidad-nombre" :title="p.correo"><a :href="'mailto:' + p.correo" class="mail-link">{{ p.correo }}</a></div>
                <span v-else class="text-muted">—</span>
              </td>
              <td>{{ p.licencia || '—' }}</td>
              <td>
                <span v-if="p.usuario" class="badge-pill badge-usuario">{{ p.usuario.name }} {{ p.usuario.apellido }}</span>
                <span v-else class="badge-pill badge-sin">Sin usuario</span>
              </td>
              <td>{{ p.contacto || '—' }}</td>
              <td class="col-acciones">
                <button class="btn-icon btn-icon-edit" @click="editarPersonal(p)" title="Editar" aria-label="Editar">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="btn-icon btn-icon-del" @click="eliminarPersonal(p)" title="Eliminar" aria-label="Eliminar">
                  <i class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
            <tr v-if="!personalFiltrado.length" class="empty-row">
              <td colspan="9">No se encontraron registros de personal</td>
            </tr>
          </tbody>
        </table>
      </div>
      <PaginacionListado :pagina="paginaActualDe(personalFiltrado)" :total-paginas="totalPaginasDe(personalFiltrado)"
        :total="personalFiltrado.length" :por-pagina="porPagina" :en-pagina="paginar(personalFiltrado).length"
        etiqueta="personas" @cambiar="pagina = $event" />
    </div>

    <!-- Modal Personal -->
    <div v-if="showModal" class="modal-bg">
      <div class="modal-box">
        <div class="modal-box-header">
          <h5><i class="bi bi-person me-2"></i>{{ modoEdicion ? 'Editar Personal' : 'Agregar Personal' }}</h5>
          <button class="modal-box-close" @click="cerrarModal"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="modal-box-body">
          <form @submit.prevent="guardarPersonal">
            <div class="form-row2 mb-3">
              <div class="field-g">
                <label class="field-lbl">Nombre <span class="req">*</span></label>
                <input type="text" class="form-control form-control-sm" v-model="form.nombre"
                  :class="{ 'is-invalid': errors.nombre }" required />
                <div v-if="errors.nombre" class="invalid-feedback">{{ errors.nombre }}</div>
              </div>
              <div class="field-g">
                <label class="field-lbl">Apellidos <span class="req">*</span></label>
                <input type="text" class="form-control form-control-sm" v-model="form.apellidos"
                  :class="{ 'is-invalid': errors.apellidos }" required />
                <div v-if="errors.apellidos" class="invalid-feedback">{{ errors.apellidos }}</div>
              </div>
            </div>

            <div class="form-row2 mb-3">
              <div class="field-g">
                <label class="field-lbl">Cargo <span class="req">*</span></label>
                <select class="form-select form-select-sm" v-model="form.cargo"
                  :class="{ 'is-invalid': errors.cargo }" required>
                  <option value="">Seleccione un cargo</option>
                  <option value="CHOFER">Chofer</option>
                  <option value="AUXILIAR">Auxiliar</option>
                  <option value="ADMINISTRATIVO">Administrativo</option>
                  <option value="OPERADOR">Operador</option>
                </select>
                <div v-if="errors.cargo" class="invalid-feedback">{{ errors.cargo }}</div>
              </div>
              <div class="field-g">
                <label class="field-lbl">Contacto</label>
                <input type="text" class="form-control form-control-sm" v-model="form.contacto" />
              </div>
            </div>

            <div class="form-row2 mb-3">
              <div class="field-g">
                <label class="field-lbl">Tipo de Documento <span class="req">*</span></label>
                <select class="form-select form-select-sm" v-model="form.tipo_doc_identidad"
                  :class="{ 'is-invalid': errors.tipo_doc_identidad }" required>
                  <option value="">Seleccione tipo</option>
                  <option value="DNI">DNI</option>
                  <option value="CE">Carné de Extranjería</option>
                  <option value="PASAPORTE">Pasaporte</option>
                </select>
                <div v-if="errors.tipo_doc_identidad" class="invalid-feedback">{{ errors.tipo_doc_identidad }}</div>
              </div>
              <div class="field-g">
                <label class="field-lbl">N° Documento <span class="req">*</span></label>
                <input type="text" class="form-control form-control-sm" v-model="form.nro_doc_identidad"
                  :class="{ 'is-invalid': errors.nro_doc_identidad }" required />
                <div v-if="errors.nro_doc_identidad" class="invalid-feedback">{{ errors.nro_doc_identidad }}</div>
              </div>
            </div>

            <div class="form-row2 mb-3">
              <div class="field-g">
                <label class="field-lbl">Correo Electrónico</label>
                <input type="email" class="form-control form-control-sm" v-model="form.correo" />
              </div>
              <div class="field-g">
                <label class="field-lbl">Licencia</label>
                <input type="text" class="form-control form-control-sm" v-model="form.licencia" />
                <small class="field-hint">Solo requerido para choferes</small>
              </div>
            </div>

            <div class="field-g mb-3">
              <label class="field-lbl">Usuario Asociado</label>
              <select class="form-select form-select-sm" v-model="form.id_usuario">
                <option value="0">Sin usuario asociado</option>
                <option v-for="u in usuarios" :key="u.id" :value="u.id">{{ u.name }} {{ u.apellido }}</option>
              </select>
              <small class="field-hint">Opcional: vincular con un usuario del sistema</small>
            </div>

            <div class="modal-box-footer">
              <button type="button" class="btn btn-sm btn-secondary" @click="cerrarModal">Cancelar</button>
              <button type="submit" class="btn-save">{{ modoEdicion ? 'Actualizar' : 'Guardar' }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'
import PaginacionListado from '@/components/PaginacionListado.vue'
import paginacionLocal from '@/mixins/paginacionLocal'

export default {
  name: 'DatosPersonalPage',
  components: { PaginacionListado },
  mixins: [paginacionLocal],
  data() {
    return {
      isLoading: false,
      personal: [],
      personalFiltrado: [],
      usuarios: [],
      showModal: false,
      modoEdicion: false,
      filtros: { nombre: '', cargo: '', documento: '' },
      form: { id_personal: null, id_usuario: 0, nombre: '', apellidos: '', cargo: '', contacto: '', tipo_doc_identidad: '', nro_doc_identidad: '', correo: '', licencia: '' },
      errors: {}
    }
  },
  mounted() {
    this.cargarPersonal()
    this.cargarUsuarios()
  },
  methods: {
    async cargarPersonal() {
      this.isLoading = true
      try {
        const r = await axios.get('/datos-personal')
        this.personal = r.data.data.data || r.data.data || []
        this.personalFiltrado = [...this.personal]
      } catch {
        Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo cargar el personal' })
      } finally { this.isLoading = false }
    },
    async cargarUsuarios() {
      try { const r = await axios.get('/users'); this.usuarios = r.data.data || r.data || [] } catch { /**/ }
    },
    filtrarPersonal() {
      this.pagina = 1
      this.personalFiltrado = this.personal.filter(p => {
        const nombre = `${p.nombre} ${p.apellidos}`.toLowerCase()
        return (!this.filtros.nombre || nombre.includes(this.filtros.nombre.toLowerCase()))
          && (!this.filtros.cargo || p.cargo === this.filtros.cargo)
          && (!this.filtros.documento || p.nro_doc_identidad.includes(this.filtros.documento))
      })
    },
    limpiarFiltros() {
      this.filtros = { nombre: '', cargo: '', documento: '' }
      this.pagina = 1
      this.personalFiltrado = [...this.personal]
    },
    abrirModalPersonal(persona = null) {
      this.modoEdicion = !!persona; this.errors = {}
      this.form = persona ? { ...persona } : { id_personal: null, id_usuario: 0, nombre: '', apellidos: '', cargo: '', contacto: '', tipo_doc_identidad: '', nro_doc_identidad: '', correo: '', licencia: '' }
      this.showModal = true
    },
    editarPersonal(p) { this.abrirModalPersonal(p) },
    cerrarModal() { this.showModal = false; this.modoEdicion = false; this.errors = {} },
    async guardarPersonal() {
      this.errors = {}
      if (!this.form.nombre.trim()) this.errors.nombre = 'El nombre es requerido'
      if (!this.form.apellidos.trim()) this.errors.apellidos = 'Los apellidos son requeridos'
      if (!this.form.cargo) this.errors.cargo = 'El cargo es requerido'
      if (!this.form.tipo_doc_identidad) this.errors.tipo_doc_identidad = 'El tipo de documento es requerido'
      if (!this.form.nro_doc_identidad.trim()) this.errors.nro_doc_identidad = 'El número de documento es requerido'
      if (Object.keys(this.errors).length > 0) return
      try {
        if (this.modoEdicion) await axios.put(`/datos-personal/${this.form.id_personal}`, this.form)
        else await axios.post('/datos-personal', this.form)
        Swal.fire({ icon: 'success', title: 'Éxito', text: `Personal ${this.modoEdicion ? 'actualizado' : 'registrado'} correctamente` })
        this.cerrarModal(); this.cargarPersonal()
      } catch (error) {
        if (error.response?.data?.errors) this.errors = error.response.data.errors
        else Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo guardar el personal' })
      }
    },
    async eliminarPersonal(persona) {
      const result = await Swal.fire({ title: '¿Estás seguro?', text: `Se eliminará a ${persona.nombre} ${persona.apellidos}`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6', confirmButtonText: 'Sí, eliminar', cancelButtonText: 'Cancelar' })
      if (result.isConfirmed) {
        try {
          await axios.delete(`/datos-personal/${persona.id_personal}`)
          Swal.fire({ icon: 'success', title: 'Eliminado', text: 'Personal eliminado correctamente' })
          this.cargarPersonal()
        } catch { Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo eliminar el personal' }) }
      }
    },
    getBadgeClass(cargo) {
      return { 'CHOFER': 'badge-chofer', 'AUXILIAR': 'badge-auxiliar', 'ADMINISTRATIVO': 'badge-admin', 'OPERADOR': 'badge-operador' }[cargo] || 'badge-default'
    }
  }
}
</script>

<style scoped>
/* Filtros (solo distribución; la tarjeta y los botones usan el estilo global) */
.filter-card { display: flex; align-items: flex-end; gap: 16px; flex-wrap: wrap; }
.filter-fields { display: flex; gap: 12px; flex-wrap: wrap; flex: 1; }
.filter-field { display: flex; flex-direction: column; gap: 4px; min-width: 160px; }
.filter-label { font-size: 0.72rem; font-weight: 700; color: #546e7a; text-transform: uppercase; letter-spacing: 0.4px; }

/* Loading */
.loading-box { padding: 40px; text-align: center; color: #90a4ae; font-size: 0.88rem; background: #fff; border-radius: 10px; border: 1px solid #e8eaf6; }

/* Tabla */
.mail-link { color: #3f51b5; text-decoration: none; font-size: 0.8rem; }
.mail-link:hover { text-decoration: underline; }

/* Badges */
.badge-pill { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.4px; text-transform: uppercase; white-space: nowrap; }
.badge-chofer      { background: #e3f2fd; color: #1565c0; }
.badge-auxiliar    { background: #e8f5e9; color: #2e7d32; }
.badge-admin       { background: #ede7f6; color: #6a1b9a; }
.badge-operador    { background: #fff3e0; color: #e65100; }
.badge-default     { background: #f5f5f5; color: #546e7a; }
.badge-usuario     { background: #e8eaf6; color: #3f51b5; }
.badge-sin         { background: #f5f5f5; color: #9e9e9e; }

/* Modal */
.modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1050; padding: 16px; }
.modal-box { background: #fff; border-radius: 12px; box-shadow: 0 20px 48px rgba(15,23,42,0.18); width: 100%; max-width: 560px; overflow: hidden; max-height: calc(100vh - 2rem); display: flex; flex-direction: column; }
.modal-box-header { background: #fff; padding: 14px 20px; border-bottom: 1px solid #eceef3; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-shrink: 0; }
.modal-box-header h5 { margin: 0; font-size: 1rem; font-weight: 600; line-height: 1.4; color: #2b2f3a !important; }
.modal-box-close { background: transparent; border: none; color: #8592a3; border-radius: 8px; width: 32px; height: 32px; margin-right: -6px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background 0.15s, color 0.15s; }
.modal-box-close:hover { background: #f0f2f5; color: #384551; }
.modal-box-body { padding: 20px; overflow-y: auto; }
.modal-box-footer { display: flex; gap: 8px; justify-content: flex-end; margin-top: 20px; padding-top: 16px; border-top: 1px solid #f0f1fa; }
.form-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.field-g { display: flex; flex-direction: column; }
.field-lbl { font-size: 0.72rem; font-weight: 700; color: #546e7a; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; }
.field-hint { font-size: 0.72rem; color: #90a4ae; margin-top: 3px; }
.req { color: #e53935; }
.btn-save { background: linear-gradient(135deg, #3f51b5 0%, #283593 100%); color: #fff; border: none; border-radius: 8px; padding: 8px 20px; font-size: 0.85rem; font-weight: 600; cursor: pointer; }
.btn-save:hover { opacity: 0.9; }
</style>
